import { FormEvent, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTheme } from "../context/ThemeContext";

const BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

type RoleForm = {
  roleName: string;
  accountAligned: string[];
  isActive: boolean;
};

type RoleRow = {
  id: number | string;
  role_name: string;
  account_aligned: string[] | string;
  is_active: boolean;
  created_by_email: string;
  updated_by_email: string;
  created_at: string;
  updated_at: string;
};

type AccountOption = {
  id: number | string;
  account_name: string;
  access_type: string;
};

type ToastState = {
  type: "success" | "error";
  message: string;
};

const EMPTY_ROLE_FORM: RoleForm = {
  roleName: "",
  accountAligned: [],
  isActive: true,
};

const PAGE_SIZE = 10;

const formatDate = (value: string): string => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const parseAccountAligned = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === "string");
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const RoleManagement = () => {
  const { isDarkMode } = useTheme();

  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [rolesCount, setRolesCount] = useState(0);
  const [rolesLoading, setRolesLoading] = useState(false);

  const [accountAlignedOptions, setAccountAlignedOptions] = useState<AccountOption[]>([]);
  const [accountAlignedLoading, setAccountAlignedLoading] = useState(false);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<RoleForm>(EMPTY_ROLE_FORM);
  const [editingRoleId, setEditingRoleId] = useState("");
  const [editForm, setEditForm] = useState<RoleForm>(EMPTY_ROLE_FORM);
  const [deleteConfirmRoleId, setDeleteConfirmRoleId] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [actionInProgress, setActionInProgress] = useState("");
  const [toast, setToast] = useState<ToastState | null>(null);
  const [page, setPage] = useState(1);

  const pageCount = Math.max(1, Math.ceil(roles.length / PAGE_SIZE));

  const pagedRoles = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return roles.slice(start, start + PAGE_SIZE);
  }, [roles, page]);

  const showToast = (type: ToastState["type"], message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 4000);
  };

  const getApiStatus = (error: unknown): number | null => {
    const status = (error as { response?: { status?: unknown } })?.response?.status;
    return typeof status === "number" ? status : null;
  };

  const getApiDetail = (error: unknown): string | null => {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
    return typeof detail === "string" ? detail : null;
  };

  const fetchAccountAlignedOptions = async () => {
    setAccountAlignedLoading(true);

    try {
      const response = await axios.get(`${BASE}/api/account-management/accounts`, {
        withCredentials: true,
      });

      const options = Array.isArray(response.data?.items)
        ? (response.data.items as AccountOption[])
        : [];
      const privateOptions = options.filter(
        (option) => option.access_type === "Private",
      );
      setAccountAlignedOptions(privateOptions);
    } catch (error) {
      console.error("Failed to load account aligned options:", error);
      setAccountAlignedOptions([]);
    } finally {
      setAccountAlignedLoading(false);
    }
  };

  const fetchRoles = async () => {
    setRolesLoading(true);

    try {
      const response = await axios.get(
        `${BASE}/api/user-management/roles?include_inactive=true`,
        { withCredentials: true },
      );

      const nextRoles = Array.isArray(response.data?.items)
        ? (response.data.items as RoleRow[])
        : [];
      setRoles(nextRoles);
      setRolesCount(Number(response.data?.count || nextRoles.length));
      setPage(1);
    } catch (error) {
      setRoles([]);
      setRolesCount(0);

      if (getApiStatus(error) === 401) {
        showToast("error", "Session expired. Please login again.");
      } else {
        showToast("error", "Failed to load roles.");
      }
    } finally {
      setRolesLoading(false);
    }
  };

  useEffect(() => {
    void fetchRoles();
    void fetchAccountAlignedOptions();
  }, []);

  const openCreate = () => {
    setCreateForm(EMPTY_ROLE_FORM);
    setEditingRoleId("");
    setDeleteConfirmRoleId("");
    setIsCreateOpen(true);
  };

  const openEdit = (role: RoleRow) => {
    setEditForm({
      roleName: role.role_name || "",
      accountAligned: parseAccountAligned(role.account_aligned),
      isActive: Boolean(role.is_active),
    });
    setEditingRoleId(String(role.id || ""));
    setDeleteConfirmRoleId("");
    setIsCreateOpen(false);
  };

  const openDelete = (role: RoleRow) => {
    setDeleteConfirmRoleId(String(role.id || ""));
    setEditingRoleId("");
    setIsCreateOpen(false);
  };

  const closeCreatePanel = () => setIsCreateOpen(false);

  const cancelInlineEdit = () => {
    setEditingRoleId("");
    setEditForm(EMPTY_ROLE_FORM);
  };

  const cancelInlineDelete = () => {
    setDeleteConfirmRoleId("");
  };

  const handleCreateFormChange = <K extends keyof RoleForm>(
    field: K,
    value: RoleForm[K] | string,
  ) => {
    if (field === "accountAligned") {
      const toggledValue = String(value);
      setCreateForm((prev) => {
        const current = Array.isArray(prev.accountAligned)
          ? prev.accountAligned
          : [];
        const isSelected = current.includes(toggledValue);
        return {
          ...prev,
          accountAligned: isSelected
            ? current.filter((item) => item !== toggledValue)
            : [...current, toggledValue],
        };
      });
    } else {
      setCreateForm((prev) => ({ ...prev, [field]: value as RoleForm[K] }));
    }
  };

  const handleEditFormChange = <K extends keyof RoleForm>(
    field: K,
    value: RoleForm[K] | string,
  ) => {
    if (field === "accountAligned") {
      const toggledValue = String(value);
      setEditForm((prev) => {
        const current = Array.isArray(prev.accountAligned)
          ? prev.accountAligned
          : [];
        const isSelected = current.includes(toggledValue);
        return {
          ...prev,
          accountAligned: isSelected
            ? current.filter((item) => item !== toggledValue)
            : [...current, toggledValue],
        };
      });
    } else {
      setEditForm((prev) => ({ ...prev, [field]: value as RoleForm[K] }));
    }
  };

  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(
        `${BASE}/api/user-management/roles`,
        {
          role_name: createForm.roleName,
          account_aligned: createForm.accountAligned.join(","),
          is_active: createForm.isActive,
        },
        {
          withCredentials: true,
        },
      );

      showToast("success", "Role created successfully.");
      setCreateForm(EMPTY_ROLE_FORM);
      setIsCreateOpen(false);
      await fetchRoles();
    } catch (error) {
      showToast("error", getApiDetail(error) || "Failed to create role.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleInlineUpdate = async (roleId: number | string) => {
    const normalizedRoleId = String(roleId).trim();
    if (!normalizedRoleId) {
      showToast("error", "Invalid role ID.");
      return;
    }

    setActionInProgress(`edit-${normalizedRoleId}`);

    try {
      await axios.put(
        `${BASE}/api/user-management/roles/${normalizedRoleId}`,
        {
          role_name: editForm.roleName,
          account_aligned: editForm.accountAligned.join(","),
          is_active: editForm.isActive,
        },
        {
          withCredentials: true,
        },
      );

      showToast("success", "Role updated successfully.");
      setEditingRoleId("");
      setEditForm(EMPTY_ROLE_FORM);
      await fetchRoles();
    } catch (error) {
      showToast("error", getApiDetail(error) || "Failed to update role.");
    } finally {
      setActionInProgress("");
    }
  };

  const handleInlineDelete = async (roleId: number | string) => {
    const normalizedRoleId = String(roleId).trim();
    if (!normalizedRoleId) {
      showToast("error", "Invalid role ID.");
      return;
    }

    setActionInProgress(`delete-${normalizedRoleId}`);

    try {
      await axios.delete(`${BASE}/api/user-management/roles/${normalizedRoleId}`, {
        withCredentials: true,
      });

      showToast("success", "Role deleted successfully.");
      setDeleteConfirmRoleId("");
      await fetchRoles();
    } catch (error) {
      showToast("error", getApiDetail(error) || "Failed to delete role.");
    } finally {
      setActionInProgress("");
    }
  };

  const inputClass =
    "w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 outline-none transition focus:border-lime-400 focus:bg-white focus:ring-2 focus:ring-lime-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-lime-500";
  const labelClass =
    "mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-300";

  const renderCreateForm = () => (
    <form onSubmit={handleCreate} className="space-y-4">
      <div>
        <label className={labelClass}>
          Role Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={createForm.roleName}
          onChange={(e) => handleCreateFormChange("roleName", e.target.value)}
          placeholder="Enter role name"
          required
          className={inputClass}
        />
      </div>

      <div>
        <label className={labelClass}>
          Account Aligned <span className="text-red-500">*</span>
        </label>
        <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
          {accountAlignedLoading ? (
            <p className="text-sm text-gray-500">Loading options...</p>
          ) : accountAlignedOptions.length === 0 ? (
            <p className="text-sm text-gray-500">No options available</p>
          ) : (
            accountAlignedOptions.map((option) => (
              <label
                key={option.id}
                className="flex cursor-pointer items-center gap-2"
              >
                <input
                  type="checkbox"
                  checked={createForm.accountAligned.includes(option.account_name)}
                  onChange={() =>
                    handleCreateFormChange("accountAligned", option.account_name)
                  }
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">
                  {option.account_name}
                </span>
              </label>
            ))
          )}
        </div>
        {createForm.accountAligned.length === 0 && (
          <p className="mt-1 text-xs text-red-500">
            At least one account aligned option must be selected
          </p>
        )}
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          role="switch"
          aria-checked={createForm.isActive}
          onClick={() => handleCreateFormChange("isActive", !createForm.isActive)}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-lime-400 focus:ring-offset-2 ${
            createForm.isActive ? "bg-lime-500" : "bg-gray-300 dark:bg-gray-600"
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              createForm.isActive ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </button>
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Is Active:{" "}
          <span className={createForm.isActive ? "text-lime-600" : "text-gray-400"}>
            {createForm.isActive ? "Yes" : "No"}
          </span>
        </span>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center rounded-xl bg-lime-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-lime-700 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? "Saving..." : "Create Role"}
        </button>
        <button
          type="button"
          onClick={closeCreatePanel}
          className="inline-flex items-center justify-center rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          Cancel
        </button>
      </div>
    </form>
  );

  return (
    <div
      className={`${
        isDarkMode ? "bg-black" : "bg-[#f4fbf6]"
      } min-h-screen w-full relative overflow-hidden`}
    >
      {!isDarkMode && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-16 -left-10 h-56 w-56 rounded-full bg-emerald-100/70 blur-3xl" />
          <div className="absolute top-20 right-10 h-48 w-48 rounded-full bg-sky-100/70 blur-3xl" />
          <div className="absolute bottom-8 left-1/3 h-44 w-44 rounded-full bg-lime-100/60 blur-3xl" />
        </div>
      )}

      <div className="relative z-10 mx-auto max-w-7xl px-6 py-10">
        <div className="text-center pb-6">
          <span className="inline-flex items-center rounded-full border border-lime-300/80 bg-lime-50 px-4 py-1 text-xs font-bold uppercase tracking-wide text-lime-800 dark:border-lime-700 dark:bg-lime-900/40 dark:text-lime-200">
            Admin Panel
          </span>
          <h2 className="mt-3 text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-white">
            Role Management
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            List of roles and quick actions to create, update, and delete.
          </p>
        </div>

        {toast && (
          <div
            className={`mb-6 flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold shadow ${
              toast.type === "success"
                ? "bg-lime-50 border border-lime-300 text-lime-800 dark:bg-lime-900/30 dark:border-lime-700 dark:text-lime-200"
                : "bg-red-50 border border-red-300 text-red-800 dark:bg-red-900/30 dark:border-red-700 dark:text-red-200"
            }`}
          >
            <span>{toast.type === "success" ? "✓" : "✕"}</span>
            <span>{toast.message}</span>
          </div>
        )}

        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Total Roles: {rolesCount}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={fetchRoles}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={openCreate}
              className="rounded-lg bg-lime-600 px-3 py-2 text-xs font-semibold text-white hover:bg-lime-700"
            >
              + Create Role
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-[#dbe7f3] bg-white shadow-[0_10px_24px_rgba(16,24,40,0.08)] dark:border-gray-800 dark:bg-gray-950">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-[#1f3550] text-white">
                <tr>
                  <th className="px-4 py-3 font-semibold">Role ID</th>
                  <th className="px-4 py-3 font-semibold">Role Name</th>
                  <th className="px-4 py-3 font-semibold">Account Aligned</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Created By</th>
                  <th className="px-4 py-3 font-semibold">Updated By</th>
                  <th className="px-4 py-3 font-semibold">Created Date</th>
                  <th className="px-4 py-3 font-semibold">Updated Date</th>
                  <th className="px-4 py-3 font-semibold text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rolesLoading && (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                    >
                      Loading roles...
                    </td>
                  </tr>
                )}

                {!rolesLoading && pagedRoles.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                    >
                      No roles found.
                    </td>
                  </tr>
                )}

                {!rolesLoading &&
                  pagedRoles.map((role, index) => {
                    const normalizedRoleId = String(role.id);
                    const isEditing = editingRoleId === normalizedRoleId;
                    const isDeleteConfirm = deleteConfirmRoleId === normalizedRoleId;
                    const serialRoleId = (page - 1) * PAGE_SIZE + index + 1;

                    return (
                      <tr
                        key={normalizedRoleId}
                        className="border-b border-[#edf3f9] text-[#5b7293] dark:border-gray-800 dark:text-gray-200"
                      >
                        <td className="px-4 py-3 font-semibold">{serialRoleId}</td>
                        <td className="px-4 py-3 font-semibold text-[#223b5a] dark:text-white">
                          {isEditing ? (
                            <input
                              value={editForm.roleName}
                              onChange={(e) =>
                                handleEditFormChange("roleName", e.target.value)
                              }
                              className="w-full rounded border border-gray-300 px-2 py-1 text-xs text-gray-900"
                            />
                          ) : (
                            role.role_name || "-"
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <div className="space-y-1 rounded border border-gray-300 bg-gray-50 p-2">
                              {accountAlignedOptions.map((option) => (
                                <label
                                  key={option.id}
                                  className="flex cursor-pointer items-center gap-2"
                                >
                                  <input
                                    type="checkbox"
                                    checked={editForm.accountAligned.includes(
                                      option.account_name,
                                    )}
                                    onChange={() =>
                                      handleEditFormChange(
                                        "accountAligned",
                                        option.account_name,
                                      )
                                    }
                                    className="rounded border-gray-300 text-lime-600"
                                  />
                                  <span className="text-xs text-gray-700">
                                    {option.account_name}
                                  </span>
                                </label>
                              ))}
                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {(() => {
                                const accountAlignedList = parseAccountAligned(
                                  role.account_aligned,
                                );

                                return accountAlignedList.length > 0 ? (
                                  accountAlignedList.map((item) => (
                                    <span
                                      key={item}
                                      className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-700"
                                    >
                                      {item}
                                    </span>
                                  ))
                                ) : (
                                  <span>-</span>
                                );
                              })()}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <button
                              type="button"
                              onClick={() =>
                                handleEditFormChange("isActive", !editForm.isActive)
                              }
                              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ${
                                editForm.isActive
                                  ? "bg-lime-100 text-lime-700"
                                  : "bg-gray-200 text-gray-600"
                              }`}
                            >
                              {editForm.isActive ? "Active" : "Inactive"}
                            </button>
                          ) : (
                            <span
                              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ${
                                role.is_active
                                  ? "bg-lime-100 text-lime-700"
                                  : "bg-gray-200 text-gray-600"
                              }`}
                            >
                              {role.is_active ? "Active" : "Inactive"}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">{role.created_by_email || "-"}</td>
                        <td className="px-4 py-3">{role.updated_by_email || "-"}</td>
                        <td className="px-4 py-3">{formatDate(role.created_at)}</td>
                        <td className="px-4 py-3">{formatDate(role.updated_at)}</td>
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <div className="flex items-center justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => void handleInlineUpdate(role.id)}
                                disabled={actionInProgress === `edit-${normalizedRoleId}`}
                                className="rounded bg-lime-600 px-2 py-1 text-xs font-semibold text-white disabled:opacity-60"
                              >
                                {actionInProgress === `edit-${normalizedRoleId}`
                                  ? "Saving"
                                  : "Save"}
                              </button>
                              <button
                                type="button"
                                onClick={cancelInlineEdit}
                                className="rounded border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-700"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : isDeleteConfirm ? (
                            <div className="flex items-center justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => void handleInlineDelete(role.id)}
                                disabled={actionInProgress === `delete-${normalizedRoleId}`}
                                className="rounded bg-red-600 px-2 py-1 text-xs font-semibold text-white disabled:opacity-60"
                              >
                                {actionInProgress === `delete-${normalizedRoleId}`
                                  ? "Deleting"
                                  : "Confirm"}
                              </button>
                              <button
                                type="button"
                                onClick={cancelInlineDelete}
                                className="rounded border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-700"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => openEdit(role)}
                                className="rounded p-1 text-sky-600 hover:bg-sky-50"
                                title="Update role"
                              >
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                  />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => openDelete(role)}
                                className="rounded p-1 text-red-600 hover:bg-red-50"
                                title="Delete role"
                              >
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between border-t border-[#edf3f9] px-4 py-3 text-sm text-gray-600 dark:border-gray-800 dark:text-gray-300">
            <span>
              Rows per page <strong>{PAGE_SIZE}</strong>
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="rounded-full border border-gray-300 px-3 py-1 text-xs font-semibold disabled:opacity-40"
              >
                Prev
              </button>
              {Array.from({ length: pageCount }, (_, index) => index + 1)
                .slice(Math.max(0, page - 3), Math.max(0, page - 3) + 5)
                .map((pageNo) => (
                  <button
                    key={pageNo}
                    type="button"
                    onClick={() => setPage(pageNo)}
                    className={`h-8 w-8 rounded-full text-xs font-bold ${
                      pageNo === page
                        ? "bg-[#1f3550] text-white"
                        : "border border-gray-300 text-gray-700"
                    }`}
                  >
                    {pageNo}
                  </button>
                ))}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                disabled={page === pageCount}
                className="rounded-full border border-gray-300 px-3 py-1 text-xs font-semibold disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {isCreateOpen && (
          <div className="mt-6 bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-[0_10px_26px_rgba(16,24,40,0.08)] p-6">
            <div className="mb-5 flex items-center gap-2 text-[15px] font-semibold text-gray-800 dark:text-gray-100">
              <span className="inline-block h-4 w-[3px] rounded-full bg-lime-500" />
              <span>Create New Role</span>
            </div>
            {renderCreateForm()}
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleManagement;